<script lang="ts">
    import cn from 'classnames';
    import { onDestroy, onMount } from 'svelte';
    import { configService } from '$lib/services';
    import { sendRegistrationEvent } from '$lib/services/gtm/methods';
    import { recaptchaService } from '$lib/services/recaptcha';

    import { sendRegistrationStats } from '@api/user/stats';
    import { Spinner, SpinnerSize } from '@components/common/spinner';
    import { InputEmail } from '@components/layout/auth/components/input-email';
    import { InputPassword } from '@components/layout/auth/components/input-password';
    import { RecaptchaV2 } from '@components/layout/auth/recaptchaV2';
    import { showTooltip } from '@components/layout/header/components/balance';
    // eslint-disable-next-line no-restricted-imports
    import { globalStore } from '@components/stores';
    import type { UserValidationError, UserValidationResult } from '@components/user';
    import { validateUserData } from '@components/user';
    // eslint-disable-next-line no-restricted-imports
    import { userStore } from '@components/user/store';
    import { getRegSource, getUserType } from '@components/utils/stats';
    import { LL } from '@i18n/i18n-svelte';
    import { SKIP_RECAPTCHA_TOKEN } from '@shared/constants';
    import { Cookie } from '@shared/cookies';
    import { reloadPage } from '@shared/routing';
    import {
        AuthMethod,
        Feature,
        RecaptchaAction,
        SignupContext,
        type UserGender,
    } from '@shared/types';
    import { EventType } from '@shared/types/stats';
    import { Button, ButtonTypes } from '@uikit/button';
    import { Font, FontTypes } from '@uikit/font';
    import { getCookie } from '@utils/cookies';

    import s from './form.module.css';

    export let gender: UserGender | undefined;
    export let place: SignupContext;
    export let getEventCustomParams: () => Record<string, string | number> = () => ({});

    let password = '';
    let email = '';
    let loading = false;
    let captchaWait = false;
    let recaptchaReset: () => void;
    let isCaptchaV2Render: boolean;
    let isEnableRecaptcha = false;
    let needTickRecaptchaError = false;

    let validationResult: UserValidationResult;

    $: emailError = validationResult?.get('email');
    $: passwordError = emailError == null ? validationResult?.get('password') : undefined;
    $: serverError = validationResult?.get('server') ?? validationResult?.get('Validation');
    $: recaptchaError = validationResult?.get('recaptchaV2') ?? validationResult?.get('recaptchaV3');

    const send = async ({
        recaptchaToken,
        recaptchaTokenV2,
    }: {
        recaptchaToken?: string;
        recaptchaTokenV2?: string;
    }) => {
        loading = true;
        needTickRecaptchaError = false;
        email = email.trim();
        const cookiesAllowedAt = getCookie(Cookie.CookiesAllowedAt);

        validationResult = validateUserData({ password, email }, { disallowEmailPlus: true });
        if (validationResult.size > 0) {
            loading = false;
            return;
        }

        try {
            await userStore.signup({
                password,
                email,
                gender,
                cookiesAllowedAt,
                recaptchaToken,
                recaptchaTokenV2,
            });
            sendRegistrationEvent({
                event: EventType.RegistrationSuccess,
                type: getUserType({ gender }),
                reg_source: getRegSource(AuthMethod.Email),
                place,
                ...getEventCustomParams(),
            });
            await sendRegistrationStats(
                EventType.RegistrationSuccess,
                {
                    user_type: getUserType({ gender }),
                    reg_source: getRegSource(AuthMethod.Email),
                },
                $globalStore.isDesktopSite
            );
            showTooltip();
            reloadPage();
        } catch (e: unknown) {
            const error = e as UserValidationError;
            if ((error as Error).cause === 'recaptchaV3') {
                isEnableRecaptcha = true;
                recaptchaReset();
            } else if ((error as Error).cause === 'recaptchaV2') {
                validationResult = validationResult ?? new Map();
                validationResult.set('recaptchaV2', (error as Error).message ?? error);
            } else {
                validationResult = new Map();
                validationResult.set(error.cause ?? 'server', error.message ?? error);
            }

            sendRegistrationEvent({
                event: EventType.RegistrationError,
                type: getUserType({ gender }),
                reg_source: getRegSource(AuthMethod.Email),
                reason: error.message,
            });
            await sendRegistrationStats(
                EventType.RegistrationError,
                {
                    user_type: getUserType({ gender }),
                    reg_source: getRegSource(AuthMethod.Email),
                    error_reason: error.message,
                },
                $globalStore.isDesktopSite
            );
        } finally {
            loading = false;
        }
    };

    async function submitForm() {
        if (captchaWait) {
            needTickRecaptchaError = true;
            return;
        }

        loading = true;
        try {
            const recaptchaToken = configService.isFeatureAllowed(Feature.DevSkipRecaptcha)
                ? SKIP_RECAPTCHA_TOKEN
                : await recaptchaService.getToken(RecaptchaAction.SignUp);
            send({ recaptchaToken });
        } catch (e: unknown) {
            validationResult = validationResult ?? new Map();
            validationResult.set('recaptchaV3', (e as Error).message ?? e);
        }
    }

    onMount(() => {
        recaptchaService.show();
    });

    onDestroy(() => {
        recaptchaService.hide();
    });
</script>

<form class={s.root} on:submit|preventDefault={submitForm}>
    {#if recaptchaError != null}
        <Font
            tagName={FontTypes.TagName.p}
            color={FontTypes.Color.Invert}
            className={cn(s.errorMessage, s.recaptchaError)}
            attrs={{ 'data-testid': 'errorMessage' }}
        >
            {recaptchaError}
        </Font>
    {/if}
    <div class={cn(s.inputsGroup)}>
        <InputEmail
            bind:value={email}
            classes={cn(s.loginInput)}
            validationError={emailError || serverError}
            isSignUp
        />
        <InputPassword
            bind:value={password}
            classes={cn(s.passwordInput)}
            validationError={passwordError || serverError}
            isSignUp
        />
        {#if emailError || passwordError || serverError}
            <Font
                tagName={FontTypes.TagName.p}
                color={FontTypes.Color.Secondary}
                className={cn(s.errorMessage)}
                attrs={{ 'data-testid': 'errorMessage' }}
            >
                {emailError || passwordError || serverError}
            </Font>
        {/if}
    </div>
    {#if isEnableRecaptcha}
        <div class={cn({ [s.recaptchaV2Show]: isCaptchaV2Render })}>
            <RecaptchaV2
                    submit={(recaptchaTokenV2) => send({ recaptchaTokenV2 })}
                    bind:captchaWait
                    bind:isRendered={isCaptchaV2Render}
                    bind:reset={recaptchaReset}
            />
            {#if needTickRecaptchaError}
                <Font
                        tagName={FontTypes.TagName.p}
                        color={FontTypes.Color.Invert}
                        className={cn(s.errorMessage, s.needTickError)}
                >
                    {$LL.validationErrors.needTickRecaptcha()}
                </Font>
            {/if}
        </div>
    {/if}
    <Button
        classes={cn(s.button)}
        color={ButtonTypes.Color.Brand}
        type={ButtonTypes.Type.Submit}
        fullWidth
        disabled={loading}
    >
        {#if !loading}
            {$LL.auth.signupEmail()}
        {:else}
            <Spinner size={SpinnerSize.Small} />
        {/if}
    </Button>
</form>
